import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanSearch, Search, Plus, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Shield, Layers, Clock, Target, Award, MessageSquare, ClipboardCheck, Calendar } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, useAchatsListViewMode, useAchatsListDisplay, fetchAchatsSuppliers, resolveAchatsEntityReference, achatsSettingsFromConfig } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const ControleQualitePage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.controleQualitePage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);
  const dateLocale = getBrandLocale(i18n.language);

  const [searchTerm, setSearchTerm] = useState('');
  const [inspections, setInspections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [selectedInspections, setSelectedInspections] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingInspection, setViewingInspection] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'results', label: p('section_results'), icon: CheckCircle, iconColor: 'text-green-400' },
    { id: 'actions', label: p('section_actions'), icon: Shield, iconColor: 'text-amber-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    inspection_reference: '', 
    inspection_date: new Date().toISOString().split('T')[0],
    inspection_type: 'incoming',
    status: 'scheduled',
    supplier_id: '',
    supplier_name: '',
    product_name: '',
    batch_number: '',
    quantity_inspected: '',
    quantity_accepted: '',
    quantity_rejected: '',
    inspector_name: '',
    category: '',
    priority: 'medium',
    department: '',
    defect_rate: '',
    conformity_rate: '',
    inspection_criteria: '',
    test_results: '',
    defects_found: '',
    corrective_actions: '',
    notes: '',
    follow_up_required: 'no',
    follow_up_date: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_inspections');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadInspections(); loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData((prev) => ({ ...prev, supplier_id: '', supplier_name: '' }));
      return;
    }
    const supplier = availableSuppliers.find((s) => String(s.id) === String(supplierId));
    if (supplier) {
      setFormData((prev) => ({
        ...prev,
        supplier_id: String(supplierId),
        supplier_name: supplier.company_name || supplier.name || '',
      }));
    }
  };

  const generateInspectionReference = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'qualityInspection', 'QC', inspections, (i) => i.inspection_reference),
    [achatsSettings, inspections],
  );
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadInspections = async () => {
    try {
      const response = await localApiAdapter.get('/achats/controle-qualite');
      setInspections(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading inspections:', error);
      setInspections([]);
    }
  };

  const stats = {
    total: inspections.length,
    scheduled: inspections.filter(i => i.status === 'scheduled').length,
    inProgress: inspections.filter(i => i.status === 'in_progress').length,
    completed: inspections.filter(i => i.status === 'completed').length,
    avgConformity: inspections.length > 0 ? (inspections.reduce((sum, i) => sum + (parseFloat(i.conformity_rate) || 0), 0) / inspections.length).toFixed(1) : 0,
    avgDefect: inspections.length > 0 ? (inspections.reduce((sum, i) => sum + (parseFloat(i.defect_rate) || 0), 0) / inspections.length).toFixed(1) : 0
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_inspections', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('mainTab_by_type'), icon: Shield },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Target },
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'scheduled', name: p('quickFilter_scheduled'), icon: Clock },
    { id: 'in_progress', name: p('quickFilter_in_progress'), icon: ScanSearch },
    { id: 'completed', name: p('quickFilter_completed'), icon: CheckCircle },
  ]), [t]);

  const inspectionDistribution = useMemo(() => ({
    all_inspections: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: stats.scheduled, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.inProgress, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [
        { filterKey: 'incoming', name: p('dist_incoming'), count: inspections.filter(i => i.inspection_type === 'incoming').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'in_process', name: p('dist_in_process'), count: inspections.filter(i => i.inspection_type === 'in_process').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'final', name: p('dist_final'), count: inspections.filter(i => i.inspection_type === 'final').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'audit', name: p('dist_audit'), count: inspections.filter(i => i.inspection_type === 'audit').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: stats.scheduled, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'in_progress', name: p('dist_in_progress'), count: stats.inProgress, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'completed', name: p('dist_completed'), count: stats.completed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  }), [t, stats.scheduled, stats.inProgress, stats.completed, inspections]);

  const distributionInfo = inspectionDistribution[mainViewMode] || inspectionDistribution.all_inspections;

  const baseFilteredInspections = inspections.filter(i => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const inspectionDate = new Date(i.inspection_date);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (inspectionDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (inspectionDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !i.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !i.inspection_reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_inspections') {
      if (quickFilter === 'scheduled' && i.status !== 'scheduled') return false;
      if (quickFilter === 'in_progress' && i.status !== 'in_progress') return false;
      if (quickFilter === 'completed' && i.status !== 'completed') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      if (mainViewMode === 'all_inspections' || mainViewMode === 'par_statut') {
        if (i.status !== activeDistributionFilter) return false;
      } else if (mainViewMode === 'par_type') {
        if (i.inspection_type !== activeDistributionFilter) return false;
      }
    }
    
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const inspectionReference = resolveAchatsEntityReference(
        achatsSettings,
        'qualityInspection',
        'QC',
        inspections,
        (i) => i.inspection_reference,
        formData.inspection_reference,
      );
      const submitData = { 
        inspection_reference: inspectionReference,
        inspection_date: formData.inspection_date,
        inspection_type: formData.inspection_type,
        status: formData.status,
        supplier_id: formData.supplier_id || null,
        supplier_name: formData.supplier_name,
        product_name: formData.product_name,
        batch_number: formData.batch_number,
        quantity_inspected: parseFloat(formData.quantity_inspected) || 0,
        quantity_accepted: parseFloat(formData.quantity_accepted) || 0,
        quantity_rejected: parseFloat(formData.quantity_rejected) || 0,
        inspector_name: formData.inspector_name,
        category: formData.category,
        priority: formData.priority,
        department: formData.department,
        defect_rate: parseFloat(formData.defect_rate) || 0,
        conformity_rate: parseFloat(formData.conformity_rate) || 0,
        inspection_criteria: formData.inspection_criteria,
        test_results: formData.test_results,
        defects_found: formData.defects_found,
        corrective_actions: formData.corrective_actions,
        notes: formData.notes,
        follow_up_required: formData.follow_up_required,
        follow_up_date: formData.follow_up_date
      };
      if (editingInspection) {
        await localApiAdapter.put(`/achats/qualite/${editingInspection.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/controle-qualite', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error') + (error.message || p('unknown_error')));
    } finally {
      resetForm();
      loadInspections();
    }
  };

  const handleEdit = (inspection) => {
    setEditingInspection(inspection);
    setFormData({ 
      inspection_reference: inspection.inspection_reference || '',
      inspection_date: inspection.inspection_date || '',
      inspection_type: inspection.inspection_type || 'incoming',
      status: inspection.status || 'scheduled',
      supplier_id: inspection.supplier_id?.toString() || '',
      supplier_name: inspection.supplier_name || '',
      product_name: inspection.product_name || '',
      batch_number: inspection.batch_number || '',
      quantity_inspected: inspection.quantity_inspected?.toString() || '',
      quantity_accepted: inspection.quantity_accepted?.toString() || '',
      quantity_rejected: inspection.quantity_rejected?.toString() || '',
      inspector_name: inspection.inspector_name || '',
      category: inspection.category || '',
      priority: inspection.priority || 'medium',
      department: inspection.department || '',
      defect_rate: inspection.defect_rate?.toString() || '',
      conformity_rate: inspection.conformity_rate?.toString() || '',
      inspection_criteria: inspection.inspection_criteria || '',
      test_results: inspection.test_results || '',
      defects_found: inspection.defects_found || '',
      corrective_actions: inspection.corrective_actions || '',
      notes: inspection.notes || '',
      follow_up_required: inspection.follow_up_required || 'no',
      follow_up_date: inspection.follow_up_date || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/qualite/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadInspections();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedInspections.length }))) {
      try {
        const deleteCount = selectedInspections.length;
        await Promise.all(selectedInspections.map(id => localApiAdapter.delete(`/achats/qualite/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedInspections([]);
        loadInspections();
      } catch (error) {
        console.error('Error deleting inspections:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      inspection_reference: '', 
      inspection_date: new Date().toISOString().split('T')[0],
      inspection_type: 'incoming',
      status: 'scheduled',
      supplier_id: '',
      supplier_name: '',
      product_name: '',
      batch_number: '',
      quantity_inspected: '',
      quantity_accepted: '',
      quantity_rejected: '',
      inspector_name: '',
      category: '',
      priority: 'medium',
      department: '',
      defect_rate: '',
      conformity_rate: '',
      inspection_criteria: '',
      test_results: '',
      defects_found: '',
      corrective_actions: '',
      notes: '',
      follow_up_required: 'no',
      follow_up_date: ''
    });
    setEditingInspection(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'inspection_reference', label: p('col_reference') },
      { key: 'inspection_date', label: p('col_inspection_date') },
      { key: 'product_name', label: p('col_product') },
      { key: 'inspection_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'quantity_inspected', label: p('label_quantity_inspected') },
      { key: 'conformity_rate', label: p('label_conformity_rate') },
      { key: 'defect_rate', label: p('label_defect_rate') }
    ];
    const fileName = `controle_qualite_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredInspections, headers, `${fileName}.xlsx`);
  };

  const handleImport = () => {
    const headers = [
      { key: 'inspection_reference', label: p('col_reference') },
      { key: 'inspection_date', label: p('col_inspection_date') },
      { key: 'product_name', label: p('col_product') },
      { key: 'inspection_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'quantity_inspected', label: p('label_quantity_inspected') }
    ];
    const schema = {
      requiredFields: ['inspection_reference', 'product_name'],
      validators: {
        quantity_inspected: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/controle-qualite', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadInspections();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedInspections([]);
    } else {
      setSelectedInspections(filteredInspections.map((r) => r.id));
    }
  };
  
  const handleSelectInspection = (id) => { 
    setSelectedInspections(selectedInspections.includes(id) ? selectedInspections.filter(iId => iId !== id) : [...selectedInspections, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/qualite/${recordId}`, { status: newStatus });
      await loadInspections();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const inspectionTypeOptions = useMemo(() => ([
    { value: 'incoming', label: p('type_incoming') },
    { value: 'in_process', label: p('type_in_process') },
    { value: 'final', label: p('type_final') },
    { value: 'audit', label: p('type_audit') },
    { value: 'sample', label: p('type_sample') },
  ]), [t]);

  const statusOptions = useMemo(() => ([
    { value: 'scheduled', label: p('status_scheduled'), icon: Calendar, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'failed', label: p('status_failed'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: AlertCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') },
  ]), [t]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('inspection_reference'),
      textColumnRule('inspection_date', (r) => (r.inspection_date ? String(r.inspection_date).split('T')[0] : '')),
      textColumnRule('product_name'),
      exactColumnRule('inspection_type', (r) => r.inspection_type || ''),
      textColumnRule('quantity_inspected', (r) => String(r.quantity_inspected ?? '')),
      textColumnRule('conformity_rate', (r) => String(r.conformity_rate ?? '')),
      exactColumnRule('priority', (r) => r.priority || ''),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [],
  );

  const { filtered: filteredInspections, displayed: displayedInspections } = useAchatsListDisplay(
    baseFilteredInspections,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedInspections, filteredInspections);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const followUpOptions = useMemo(() => ([
    { value: 'no', label: p('follow_up_no') },
    { value: 'yes', label: p('follow_up_yes') },
  ]), [t]);

  const categoryOptions = useMemo(() => ([
    { value: 'finished_products', label: p('category_finished_products') },
    { value: 'matieres_premieres', label: p('category_matiere_premiere') },
    { value: 'components', label: p('category_components') },
    { value: 'packaging', label: p('category_packaging') },
  ]), [t]);

  const controleColumnKeys = useMemo(
    () => ['inspection_reference', 'inspection_date', 'product_name', 'inspection_type', 'quantity_inspected', 'conformity_rate', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(controleColumnKeys);
  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'inspection_reference', labelKey: 'col_reference' },
      { key: 'inspection_date', labelKey: 'col_inspection_date' },
      { key: 'product_name', labelKey: 'col_product' },
      { key: 'inspection_type', labelKey: 'col_type' },
      { key: 'quantity_inspected', labelKey: 'col_quantity' },
      { key: 'conformity_rate', labelKey: 'col_conformity' },
      { key: 'priority', labelKey: 'col_priority' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ScanSearch className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadInspections} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={() => {
              setEditingInspection(null);
              setFormTab('general');
              setFormData({
                inspection_reference: generateInspectionReference(),
                inspection_date: new Date().toISOString().split('T')[0],
                inspection_type: 'incoming',
                status: 'scheduled',
                supplier_id: '',
                supplier_name: '',
                product_name: '',
                batch_number: '',
                quantity_inspected: '',
                quantity_accepted: '',
                quantity_rejected: '',
                inspector_name: '',
                category: '',
                priority: 'medium',
                department: '',
                defect_rate: '',
                conformity_rate: '',
                inspection_criteria: '',
                test_results: '',
                defects_found: '',
                corrective_actions: '',
                notes: '',
                follow_up_required: 'no',
                follow_up_date: '',
              });
              setShowModal(true);
            }}
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: ScanSearch, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_scheduled'), value: stats.scheduled, icon: Clock, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_in_progress'), value: stats.inProgress, icon: ClipboardCheck, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_completed'), value: stats.completed, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_conformity_rate'), value: `${stats.avgConformity}%`, icon: Award, color: 'from-green-500 to-green-600' },
          { title: p('stat_defect_rate'), value: `${stats.avgDefect}%`, icon: AlertCircle, color: 'from-red-500 to-red-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <motion.div 
        initial={false} 
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} 
        transition={{ duration: 0.3, ease: "easeInOut" }} 
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { 
                    setMainViewMode(tab.id); 
                    setQuickFilter('all'); 
                    setActiveDistributionFilter(null); 
                  }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filter Buttons (only for Vue Générale) */}
          {mainViewMode === 'all_inspections' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.filterKey || item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.name 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_by_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>
                      {item.name}
                    </h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div 
                      className={`h-3 rounded-full ${item.color} transition-all`} 
                      style={{ 
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` 
                      }}
                    ></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}${p('percent_of_inspections')}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Container - Continuing with table, card views, and modals */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <AchatsBulkDeleteBar
          selectedCount={selectedInspections.length}
          onDelete={handleBulkDelete}
          deleteLabel={`${t('common.delete')} (${selectedInspections.length})`}
        />

        {/* Table View - Due to message length limit, I'll provide a shorter version showing the structure */}
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('inspection_reference') ? <TableColumnHeaderFilter {...colHeader('inspection_reference', p('col_reference'))} /> : null}
                  {isColumnVisible('inspection_date') ? <TableColumnHeaderFilter {...colHeader('inspection_date', p('col_inspection_date'))} /> : null}
                  {isColumnVisible('product_name') ? <TableColumnHeaderFilter {...colHeader('product_name', p('col_product'))} /> : null}
                  {isColumnVisible('inspection_type') ? <TableColumnHeaderFilter {...colHeader('inspection_type', p('col_type'), { kind: 'select', options: inspectionTypeOptions })} /> : null}
                  {isColumnVisible('quantity_inspected') ? <TableColumnHeaderFilter {...colHeader('quantity_inspected', p('col_quantity'))} /> : null}
                  {isColumnVisible('conformity_rate') ? <TableColumnHeaderFilter {...colHeader('conformity_rate', p('col_conformity'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('col_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedInspections.length > 0 ? (
                  displayedInspections.map((inspection, index) => (
                    <tr 
                      key={inspection.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedInspections.includes(inspection.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedInspections.includes(inspection.id)}
                              onChange={() => handleSelectInspection(inspection.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('inspection_reference') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{inspection.inspection_reference}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('inspection_date') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString(dateLocale) : '-'}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('product_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{inspection.product_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('card_batch')} {inspection.batch_number || '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('inspection_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">{inspectionTypeOptions.find(t => t.value === inspection.inspection_type)?.label || inspection.inspection_type}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('quantity_inspected') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{inspection.quantity_inspected || 0}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('card_accepted')} {inspection.quantity_accepted || 0} | {p('card_rejected')} {inspection.quantity_rejected || 0}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('conformity_rate') ? (
                      <td className="px-6 py-4 text-center">
                        <span className="text-green-300 font-semibold">{inspection.conformity_rate || 0}%</span>
                      </td>
                      ) : null}
                      {isColumnVisible('priority') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getPriorityBadge(inspection.priority)}</div>
                      </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={inspection.status} recordId={inspection.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingInspection(inspection); setShowViewModal(true); }}
                          onEdit={() => handleEdit(inspection)}
                          onDelete={() => handleDelete(inspection.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <ScanSearch size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedInspections.length > 0 ? (
              displayedInspections.map((inspection, index) => (
                <motion.div
                  key={inspection.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ScanSearch size={20} className="text-blue-400" />
                          {inspection.product_name}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge status={inspection.status} recordId={inspection.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          {getPriorityBadge(inspection.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_reference')}:</span> {inspection.inspection_reference}</div>
                            <div><span className="font-medium">{p('col_type')}:</span> {inspectionTypeOptions.find(t => t.value === inspection.inspection_type)?.label}</div>
                            <div><span className="font-medium">{p('label_date')}</span> {inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString(dateLocale) : '-'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_quantities')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_inspected_short')}</span> {inspection.quantity_inspected || 0}</div>
                            <div><span className="font-medium">{p('card_accepted')}</span> {inspection.quantity_accepted || 0}</div>
                            <div><span className="font-medium">{p('card_rejected')}</span> <span className="text-red-300">{inspection.quantity_rejected || 0}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_results')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_conformity')}</span> <span className="text-green-300">{inspection.conformity_rate || 0}%</span></div>
                            <div><span className="font-medium">{p('card_defects')}</span> <span className="text-red-300">{inspection.defect_rate || 0}%</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_other')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_inspector')}:</span> {inspection.inspector_name || '-'}</div>
                            <div><span className="font-medium">{p('label_supplier_name')}:</span> {inspection.supplier_name || '-'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingInspection(inspection); setShowViewModal(true); }}
                      onEdit={() => handleEdit(inspection)}
                      onDelete={() => handleDelete(inspection.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <ScanSearch size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal and Edit Modal - Due to length, showing condensed structure */}
      {showViewModal && viewingInspection ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('col_reference')}</p>
                      <p className={m.viewValue}>{viewingInspection.inspection_reference}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_product')}</p>
                      <p className={m.viewValue}>{viewingInspection.product_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_type')}</p>
                      <p className={m.viewValue}>{inspectionTypeOptions.find(t => t.value === viewingInspection.inspection_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_status')}</p>
                      <div className="flex">{getStatusBadge(viewingInspection.status)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingInspection.priority)}</div>
                    </div>
                  </div>
                </div>

                {viewingInspection.defects_found && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('section_defects')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingInspection.defects_found}</p>
                  </div>
                )}

                {viewingInspection.corrective_actions && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_corrective_actions')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingInspection.corrective_actions}</p>
                  </div>
                )}

                {viewingInspection.notes && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('heading_notes')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingInspection.notes}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - Condensed for length */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingInspection ? p('modal_edit') : p('modal_new')}
        icon={ScanSearch}
        formId="inspection-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingInspection ? p('modal_edit') : p('modal_new')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="inspection-form" onSubmit={handleSubmit}>
              {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_reference_required')} /></label>
                    <input
                      type="text"
                      required
                      value={formData.inspection_reference}
                      onChange={(e) => setFormData({ ...formData, inspection_reference: e.target.value })}
                      className={`${cfgTheme.input} font-mono`}
                      placeholder={p('placeholder_ref')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_inspection_date_required')} /></label>
                    <CustomDatePicker
                      value={formData.inspection_date}
                      onChange={(date) => setFormData({ ...formData, inspection_date: date })}
                      placeholder={p('select_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_inspection_type_required')} /></label>
                    <CustomSelect
                      value={formData.inspection_type}
                      onChange={(value) => setFormData({ ...formData, inspection_type: value })}
                      options={inspectionTypeOptions}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_status_required')} /></label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={statusOptions.map((option) => ({ value: option.value, label: option.label }))}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_priority_required')} /></label>
                    <CustomSelect
                      value={formData.priority}
                      onChange={(value) => setFormData({ ...formData, priority: value })}
                      options={priorityOptions}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_category')}</label>
                    <CustomSelect
                      value={formData.category}
                      onChange={(value) => setFormData({ ...formData, category: value })}
                      options={categoryOptions}
                      placeholder={p('select_or_type')}
                      searchable
                      creatable
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_department')}</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_department')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_supplier_name')}</label>
                    {loadingSuppliers ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading', { defaultValue: 'جاري التحميل...' })}</div>
                    ) : (
                      <CustomSelect
                        value={formData.supplier_id}
                        onChange={(value) => handleSupplierSelection(value)}
                        options={availableSuppliers.map((supplier) => ({
                          value: String(supplier.id),
                          label: `${supplier.company_name || supplier.name || p('label_supplier_name')}${supplier.contact_person ? ` — ${supplier.contact_person}` : ''}`,
                        }))}
                        placeholder={p('placeholder_supplier_name')}
                        searchable
                      />
                    )}
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_product_name')} required /></label>
                    <input
                      type="text"
                      required
                      value={formData.product_name}
                      onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_product')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_batch_number')}</label>
                    <input
                      type="text"
                      value={formData.batch_number}
                      onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_batch')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_inspector')}</label>
                    <input
                      type="text"
                      value={formData.inspector_name}
                      onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_inspector')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_quantity_inspected')}</label>
                    <CustomNumberInput
                      value={formData.quantity_inspected}
                      onChange={(value) => setFormData({ ...formData, quantity_inspected: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_quantity_accepted')}</label>
                    <CustomNumberInput
                      value={formData.quantity_accepted}
                      onChange={(value) => setFormData({ ...formData, quantity_accepted: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_quantity_rejected')}</label>
                    <CustomNumberInput
                      value={formData.quantity_rejected}
                      onChange={(value) => setFormData({ ...formData, quantity_rejected: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_conformity_rate')}</label>
                    <CustomNumberInput
                      value={formData.conformity_rate}
                      onChange={(value) => setFormData({ ...formData, conformity_rate: value })}
                      placeholder="0"
                      step={0.1}
                      min={0}
                      max={100}
                      decimals={1}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_defect_rate')}</label>
                    <CustomNumberInput
                      value={formData.defect_rate}
                      onChange={(value) => setFormData({ ...formData, defect_rate: value })}
                      placeholder="0"
                      step={0.1}
                      min={0}
                      max={100}
                      decimals={1}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_inspection_criteria')}</label>
                    <textarea
                      value={formData.inspection_criteria}
                      onChange={(e) => setFormData({ ...formData, inspection_criteria: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_inspection_criteria')}
                    />
                  </div>
                </div>
              )}

              {formTab === 'results' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_test_results')}</label>
                    <textarea
                      value={formData.test_results}
                      onChange={(e) => setFormData({ ...formData, test_results: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_test_results')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_defects_found')}</label>
                    <textarea
                      value={formData.defects_found}
                      onChange={(e) => setFormData({ ...formData, defects_found: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_defects')}
                    />
                  </div>
                </div>
              )}

              {formTab === 'actions' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_corrective_actions')}</label>
                    <textarea
                      value={formData.corrective_actions}
                      onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_corrective')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_follow_up_required')} /></label>
                    <CustomSelect
                      value={formData.follow_up_required}
                      onChange={(value) => setFormData({ ...formData, follow_up_required: value })}
                      options={followUpOptions}
                      placeholder={p('select_or_type')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_follow_up_date')}</label>
                    <CustomDatePicker
                      value={formData.follow_up_date}
                      onChange={(date) => setFormData({ ...formData, follow_up_date: date })}
                      placeholder={p('select_date')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('heading_notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[150px]"
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </div>
              )}

              </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default ControleQualitePage;

